import React, { useCallback, useEffect, useState } from 'react';
import { useSetPage, useTypedTranslation } from '@app/hooks';
import {useDispatch, useSelector} from 'react-redux';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import { genericErrorToast, getMenuItemById } from '@app/helpers';
import { IListPageBarEnabled, IVaultPasswordPolicy } from '@app/models';
import VaultsSection from '@app/pages/passwordManager/components/vaultsList/vaultsSection.component';
import VaultsInfoSection from '@app/pages/passwordManager/components/vaultInfo/vaultsInfoSection.component';
import Icon from '@app/components/Icon';
import { IVaultItem } from '@app/pages/passwordManager/interfaces';
import { setExceptionHandler } from '@app/store/actions';
import { passwordManagerPolicyService, vaultsServiceNew } from '@app/services';

const setListPageBarEnabledOptions: IListPageBarEnabled = {
    changeViewEnabled: false,
    filtersViewEnabled: false,
    orderByEnabled: true,
    actionMenuEnabled: false
};

const VaultManagerPage = () => {
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();
    const [loadingVaultsData, setLoadingVaultsData] = useState(false);
    const currentOrgId = useSelector((state: any) => state.organizationSelect.selectedOrganization);

    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.VaultManager2);
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [isOpenDetailsPanel, setIsOpenDetailsPanel] = useState<boolean>(false);
    const [openDetailsPanelId, setOpenDetailsPanelId] = useState<string | null>();
    const [activeFolderId, setActiveFolderId] = useState<string>('');
    const [activeVaultId, setActiveVaultId] = useState<string>('');
    const [passwordPolicies, setPasswordPolicies] = useState<IVaultPasswordPolicy[]>([]);
    const [allVaults, setAllVaults] = useState<IVaultItem[]>([]);
    const [vaultsReloaded, setVaultsReloaded] = useState(false);
    const [reloadAllVaults, setReloadAllVaults] = useState(false);
    const [orgAllowedToViewThisPage, setOrgAllowedToViewThisPage] = useState<boolean>(true);

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        passwordManagerPolicyService.getAllPolicies2().then(handleVaultPasswordPolicySuccess).catch(genericErrorToast);
    }, []);

    useEffect(() => {
        if (reloadAllVaults) {
            setReloadAllVaults(false);
            dispatch(setExceptionHandler(true));
            setLoadingVaultsData(true);
            setVaultsReloaded(false);
            vaultsServiceNew.getAllVaults().then(handleGetAllVaultsSuccess).catch(handleGetAllVaultsError);
        }
    }, [reloadAllVaults]);

    useEffect(() => {
        if (currentOrgId) {
            setLoadingVaultsData(true);
            setVaultsReloaded(false);
            vaultsServiceNew.getAllVaults().then(handleGetAllVaultsSuccess).catch(handleGetAllVaultsError);
        }
    }, [currentOrgId]);

    const handleGetAllVaultsSuccess = (vaults: IVaultItem[]) => {
        if (!vaults.length) {
            setActiveFolderId('');
            setActiveVaultId('');
        }

        setAllVaults(vaults);
        setVaultsReloaded(true);
        setLoadingVaultsData(false);
    };

    const handleGetAllVaultsError = (error: any) => {
        setAllVaults([]);
        setLoadingVaultsData(false);

        if (error.response.status === 400 && error.response.data.error.message === 'ORG_CANNOT_VIEW') {
            setOrgAllowedToViewThisPage(false);
        } else {
            genericErrorToast(error);
        }
    };

    const handleVaultPasswordPolicySuccess = useCallback((values: IVaultPasswordPolicy[]) => {
        setPasswordPolicies(values);
    }, []);

    const showDetailsPanel = (e: any) => {
        setIsOpenDetailsPanel(true);
        setOpenDetailsPanelId(e.currentTarget.dataset.id);
    };

    const hideRightPanel = () => {
        setIsOpenDetailsPanel(false);
        setOpenDetailsPanelId(null);
        // resetFormState();
    };

    const handleFolderSuccess = () => {
        return;
    };

    const onFolderChoose = (vaultId: string, folderId: string) => {
        // setLoadingData(true);
        setActiveFolderId(folderId);
        setActiveVaultId(vaultId);
        // vaultFoldersServiceNew.getFolderPasswords(folderId).then(handleFolderSuccess);
    };

    const changeVaultNameAfterEdit = (vaultId: string, newDisplayName: string) => {
        setAllVaults((vaults) =>
            vaults.map((vault) => {
                if (vault.id === vaultId) {
                    return { ...vault, displayName: newDisplayName };
                }
                return vault;
            })
        );
    };

    const vaultDidntChosen = () => {
        return (
            <div className='vaults__info-empty'>
                <div className='vaults__info-empty-icon'>
                    <Icon icon='emptyInboxIco' />
                </div>
                <div style={{ fontWeight: '500', margin: '0 0 10px' }}>{translation('SELECT_A_VAULT_TITLE')}</div>
                <div>{translation('SELECT_A_VAULT_DESCRIPTION')}</div>
            </div>
        );
    };

    return (
        <>
            <div className='vaults__content'>
                <div className='vaults__list'>
                    <VaultsSection
                        onFolderChoose={onFolderChoose}
                        passwordPolicies={passwordPolicies}
                        reloadAllVaults={setReloadAllVaults}
                        allVaults={allVaults}
                        orgAllowedToViewThisPage={orgAllowedToViewThisPage}
                        loadingVaultsData={loadingVaultsData}
                        vaultsReloaded={vaultsReloaded}
                    />
                </div>
                <div className='vaults__info'>
                    {activeFolderId && activeVaultId ? (
                        <VaultsInfoSection
                            vaultId={activeVaultId}
                            folderId={activeFolderId}
                            passwordPolicies={passwordPolicies}
                            allVaults={allVaults}
                            changeVaultNameAfterEdit={changeVaultNameAfterEdit}
                        />
                    ) : (
                        vaultDidntChosen()
                    )}
                </div>
            </div>
        </>
    );
};

export default VaultManagerPage;
